import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { FREQUENCY_MAX_DAYS, FREQUENCY_MAX_MONTHS, KIND_LABELS, todayKey, type ClientSummary, type Intervention, type Photo, type User } from "@techplanner/shared/core";
import { api, errorMessage, newIdempotencyKey } from "../lib/api";
import { clientColor, clientInitials, dateParts, dayMoment, formatDuration, formatPhone, splitClientName, technicianNames, telHref, whenLabel } from "../lib/format";
import { normalizeSearch } from "../lib/hooks";
import { cleanAddress, hasPlace, navigationLinks, placeOf, type Place } from "../lib/maps";
import { preparePhoto } from "../lib/photos";
import { useClients } from "../lib/queries";
import { Icon } from "./Icon";
import { AvatarStack, Empty, IconButton, PriorityBadge, Sheet, StatusBadge, Stepper, useToast } from "./ui";

/* ---------- Carte d'intervention ---------- */

export function InterventionCard({ item, showDate = false, hideClient = false }: { item: Intervention; showDate?: boolean; hideClient?: boolean }) {
  const today = todayKey();
  const overdue = item.status === "planned" && item.plannedDate !== null && item.plannedDate < today;
  const moment = dayMoment(item);
  const date = showDate && item.plannedDate ? dateParts(item.plannedDate, today) : null;
  const report = item.status === "done" ? item.report?.trim() : null;
  const description = item.description?.trim();
  const label = [
    item.clientName,
    KIND_LABELS[item.kind],
    item.title,
    item.plannedDate ? `${whenLabel(item, today)}${moment ? ` ${moment.toLowerCase()}` : ""}` : "à planifier",
    item.technicians.length ? technicianNames(item.technicians) : null
  ]
    .filter(Boolean)
    .join(", ");
  const hasMeta =
    item.status !== "planned" || overdue || item.priority !== "normal" || item.kitLabel || item.privateToUserId || item.noteCount > 0 || item.photoCount > 0;
  return (
    <Link to={`/interventions/${item.id}`} className={`ivc status-${item.status} ${overdue ? "overdue" : ""}`} aria-label={label}>
      <div className="ivc-when">
        {!item.plannedDate ? (
          <span className="ivc-unplanned">À planifier</span>
        ) : (
          <>
            {date && (
              <>
                <span className="ivc-wd">{date.top}</span>
                <strong className="ivc-day">{date.day}</strong>
                <span className="ivc-mon">{date.month}</span>
                {date.year && <span className="ivc-mon">{date.year}</span>}
              </>
            )}
            {moment && <span className={`ivc-moment ${moment === "Matin" ? "am" : moment === "Après-midi" ? "pm" : "day"}`}>{moment}</span>}
          </>
        )}
      </div>
      <div className="ivc-body">
        <div className="ivc-head">
          <span className="ivc-client">{hideClient ? item.title || KIND_LABELS[item.kind] : item.clientName}</span>
          <span className={`badge kind-${item.kind}`}>{KIND_LABELS[item.kind]}</span>
        </div>
        {item.title && !hideClient && <div className="ivc-title">{item.title}</div>}
        {description && <div className="ivc-desc">{description}</div>}
        {report && (
          <div className="ivc-desc ivc-report">
            <Icon name="checkCircle" size={14} />
            <span>{report}</span>
          </div>
        )}
        <div className="ivc-techs">
          {item.technicians.length ? (
            item.technicians.map((t) => (
              <span key={t.id} className="ivc-tech">
                <span className="dot" style={{ background: t.color ?? "var(--text-3)" }} />
                {t.displayName}
              </span>
            ))
          ) : (
            <span className="muted">Aucun technicien</span>
          )}
        </div>
        {hasMeta && (
          <div className="ivc-meta">
            {overdue ? <span className="badge danger">En retard</span> : item.status !== "planned" && <StatusBadge status={item.status} />}
            <PriorityBadge priority={item.priority} />
            {item.kitLabel && (
              <span className="badge brand">
                <Icon name="box" size={13} />
                {item.kitLabel}
              </span>
            )}
            {item.privateToUserId && (
              <span className="badge" title="Privée">
                <Icon name="lock" size={13} />
              </span>
            )}
            {(item.noteCount > 0 || item.photoCount > 0) && (
              <span className="tiny muted row" style={{ gap: 6 }}>
                {item.noteCount > 0 && <span className="row" style={{ gap: 2 }}><Icon name="note" size={14} />{item.noteCount}</span>}
                {item.photoCount > 0 && <span className="row" style={{ gap: 2 }}><Icon name="camera" size={14} />{item.photoCount}</span>}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

export function InterventionList({ items, showDate, hideClient, empty }: { items: Intervention[]; showDate?: boolean; hideClient?: boolean; empty?: ReactNode }) {
  if (!items.length) return <>{empty ?? null}</>;
  return (
    <div>
      {items.map((item) => (
        <InterventionCard key={item.id} item={item} showDate={showDate} hideClient={hideClient} />
      ))}
    </div>
  );
}

/* ---------- Fréquence d'entretien (mois + jours) ---------- */

export function FrequencyInput({ months, days, onChange }: { months: number; days: number; onChange: (months: number, days: number) => void }) {
  return (
    <div className="freq-input">
      <div className="freq-cell">
        <span className="freq-cell-label">Mois</span>
        <Stepper label="Mois" editable value={months} min={0} max={FREQUENCY_MAX_MONTHS} onChange={(m) => onChange(m, days)} />
      </div>
      <div className="freq-cell">
        <span className="freq-cell-label">Jours</span>
        <Stepper label="Jours" editable value={days} min={0} max={FREQUENCY_MAX_DAYS} onChange={(d) => onChange(months, d)} />
      </div>
    </div>
  );
}

/* ---------- Pastille client ---------- */

export function ClientAvatar({ id, name, large }: { id: number; name: string; large?: boolean }) {
  return (
    <span className={`client-avatar ${large ? "l" : ""}`} style={{ ["--avatar" as string]: clientColor(id) }} aria-hidden="true">
      {clientInitials(name)}
    </span>
  );
}

/** Nom du client avec la forme juridique (GAEC, EARL…) en petit devant. */
export function ClientName({ name }: { name: string }) {
  const { form, rest } = splitClientName(name);
  return (
    <>
      {form && <span className="client-form">{form}</span>}
      {rest}
    </>
  );
}

/* ---------- Choix des techniciens ---------- */

export function TechnicianPicker({
  users,
  value,
  onChange,
  single
}: {
  users: User[];
  value: number[];
  onChange: (ids: number[]) => void;
  single?: boolean;
}) {
  const toggle = (id: number) => {
    if (single) return onChange(value[0] === id ? [] : [id]);
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };
  return (
    <div className="chips" role="group" aria-label="Techniciens">
      {users
        .filter((u) => u.active)
        .map((u) => (
          <button key={u.id} type="button" className="chip" aria-pressed={value.includes(u.id)} onClick={() => toggle(u.id)}>
            <span className="avatar" style={{ ["--avatar" as string]: u.color ?? undefined, ["--size" as string]: "22px" }}>
              {u.displayName.slice(0, 1).toUpperCase()}
            </span>
            {u.displayName}
          </button>
        ))}
    </div>
  );
}

/** Liste déroulante pour ajouter un technicien ; les choisis s'affichent au-dessus et se retirent d'un appui. */
export function TechnicianSelect({ users, value, onChange }: { users: User[]; value: number[]; onChange: (ids: number[]) => void }) {
  const byId = new Map(users.map((u) => [u.id, u]));
  const selected = value.map((id) => byId.get(id)).filter((u): u is User => Boolean(u));
  const available = users.filter((u) => u.active && !value.includes(u.id));
  return (
    <div className="stack-s">
      {selected.length > 0 && (
        <div className="chips" role="group" aria-label="Techniciens choisis">
          {selected.map((u) => (
            <button
              key={u.id}
              type="button"
              className="chip on"
              onClick={() => onChange(value.filter((v) => v !== u.id))}
              aria-label={`Retirer ${u.displayName}`}
            >
              <span className="avatar" style={{ ["--avatar" as string]: u.color ?? undefined, ["--size" as string]: "22px" }}>
                {u.displayName.slice(0, 1).toUpperCase()}
              </span>
              {u.displayName}
              <Icon name="close" size={14} />
            </button>
          ))}
        </div>
      )}
      <select
        className="select"
        aria-label="Ajouter un technicien"
        value=""
        disabled={!available.length}
        onChange={(e) => {
          const id = Number(e.target.value);
          if (id) onChange([...value, id]);
        }}
      >
        <option value="">{available.length ? (selected.length ? "Ajouter un autre technicien…" : "Choisir un technicien…") : "Tous les techniciens sont ajoutés"}</option>
        {available.map((u) => (
          <option key={u.id} value={u.id}>
            {u.displayName}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ---------- Choix du client ---------- */

export function ClientPicker({ value, onChange, error }: { value: number | null; onChange: (c: ClientSummary) => void; error?: string }) {
  const clients = useClients();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = clients.data?.find((c) => c.id === value);
  const filtered = useMemo(() => {
    const n = normalizeSearch(q);
    const list = clients.data ?? [];
    if (!n) return list.slice(0, 60);
    return list.filter((c) => normalizeSearch(`${c.name} ${c.contactName ?? ""} ${c.address ?? ""}`).includes(n)).slice(0, 60);
  }, [clients.data, q]);

  return (
    <>
      <button
        type="button"
        className="input"
        style={{ textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}
        onClick={() => setOpen(true)}
        aria-invalid={error ? true : undefined}
        aria-haspopup="dialog"
      >
        <Icon name="building" size={20} className="muted" />
        <span className={`grow ellipsis ${selected ? "" : "muted"}`}>{selected?.name ?? "Choisir un client"}</span>
        <Icon name="chevronDown" size={20} className="muted" />
      </button>
      <Sheet open={open} title="Choisir un client" onClose={() => setOpen(false)}>
        <div className="search" style={{ marginBottom: 12 }}>
          <Icon name="search" size={20} />
          <input
            className="input"
            type="search"
            placeholder="Nom, contact, commune…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            enterKeyHint="search"
          />
        </div>
        {filtered.length ? (
          <div className="list">
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                className="list-item"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setQ("");
                }}
              >
                <div className="list-item-body">
                  <div className="list-item-title">{c.name}</div>
                  {cleanAddress(c.address) && <div className="list-item-sub">{cleanAddress(c.address)}</div>}
                </div>
                {c.id === value && <Icon name="check" className="brand" />}
              </button>
            ))}
          </div>
        ) : (
          <Empty icon="search" title="Aucun client trouvé">
            <Link to="/clients/new">Créer un nouveau client</Link>
          </Empty>
        )}
      </Sheet>
    </>
  );
}

/* ---------- Appeler / Itinéraire ---------- */

export function ContactActions({ phone, place, extra }: { phone: string | null; place: Place; extra?: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const canNavigate = hasPlace(placeOf(place));
  return (
    <>
      <div className="quick-actions">
        <a className="quick-action" href={phone ? telHref(phone) : undefined} aria-disabled={!phone}>
          <Icon name="phone" />
          {phone ? "Appeler" : "Pas de n°"}
        </a>
        <button type="button" className="quick-action" onClick={() => setNavOpen(true)} aria-disabled={!canNavigate} disabled={!canNavigate}>
          <Icon name="navigation" />
          GPS
        </button>
        {extra}
      </div>
      <NavigationSheet place={navOpen ? place : null} onClose={() => setNavOpen(false)} />
    </>
  );
}

/** Choix de l'application de navigation (Google Maps, Waze, Plans). */
export function NavigationSheet({ place, onClose }: { place: Place | null; onClose: () => void }) {
  const target = place ? placeOf(place) : null;
  return (
    <Sheet open={target !== null} title="Ouvrir le GPS avec…" onClose={onClose}>
      {target?.address && <p className="small muted pre" style={{ marginBottom: 12 }}>{target.address}</p>}
      {target && (
        <div className="list">
          {navigationLinks(target).map((link) => (
            <a key={link.id} className="list-item" href={link.href} target="_blank" rel="noreferrer" onClick={onClose}>
              <span className="list-item-icon">
                <Icon name="navigation" size={20} />
              </span>
              <span className="list-item-body list-item-title">{link.label}</span>
              <Icon name="chevron" className="chevron" />
            </a>
          ))}
        </div>
      )}
    </Sheet>
  );
}

export function PhoneLink({ phone }: { phone: string }) {
  return <a href={telHref(phone)}>{formatPhone(phone)}</a>;
}

/* ---------- Photos ---------- */

interface PendingUpload {
  id: string;
  preview: string;
  error?: string;
  file: File;
}

export function PhotoGallery({
  photos,
  uploadPath,
  onUploaded,
  canDelete,
  onDelete
}: {
  photos: Photo[];
  uploadPath: string;
  onUploaded: () => void;
  canDelete: (p: Photo) => boolean;
  onDelete: (p: Photo) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [viewer, setViewer] = useState<Photo | null>(null);
  const toast = useToast();

  async function upload(entry: PendingUpload) {
    setPending((list) => list.map((p) => (p.id === entry.id ? { ...p, error: undefined } : p)));
    try {
      const { blob, width, height } = await preparePhoto(entry.file);
      const form = new FormData();
      form.append("width", String(width));
      form.append("height", String(height));
      form.append("photo", blob, "photo.jpg");
      await api(uploadPath, { method: "POST", body: form, idempotencyKey: entry.id });
      setPending((list) => list.filter((p) => p.id !== entry.id));
      URL.revokeObjectURL(entry.preview);
      onUploaded();
    } catch (err) {
      setPending((list) => list.map((p) => (p.id === entry.id ? { ...p, error: errorMessage(err) } : p)));
      toast(`Photo non envoyée : ${errorMessage(err)}`, "error");
    }
  }

  function onFiles(files: FileList | null) {
    if (!files) return;
    const entries = [...files].map((file) => ({ id: newIdempotencyKey(), preview: URL.createObjectURL(file), file }));
    setPending((list) => [...list, ...entries]);
    entries.forEach((entry) => void upload(entry));
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <>
      <div className="photo-grid">
        <button type="button" className="photo-tile add" onClick={() => inputRef.current?.click()}>
          <Icon name="camera" size={26} />
          Ajouter
        </button>
        {pending.map((p) => (
          <button key={p.id} type="button" className="photo-tile" onClick={() => p.error && void upload(p)} aria-label={p.error ? "Réessayer l'envoi" : "Envoi en cours"}>
            <img src={p.preview} alt="" />
            <span className="uploading">{p.error ? <Icon name="refresh" /> : <span className="spinner" />}</span>
          </button>
        ))}
        {photos.map((photo) => (
          <button key={photo.id} type="button" className="photo-tile" onClick={() => setViewer(photo)} aria-label="Agrandir la photo">
            <img src={photo.url} alt="" loading="lazy" decoding="async" />
          </button>
        ))}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
      {viewer && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="Photo">
          <div className="lightbox-bar">
            <IconButton icon="close" label="Fermer" onClick={() => setViewer(null)} />
            <span className="small" style={{ alignSelf: "center" }}>
              {viewer.author?.displayName} · {new Date(viewer.createdAt).toLocaleDateString("fr-FR")}
            </span>
            {canDelete(viewer) ? (
              <IconButton
                icon="trash"
                label="Supprimer la photo"
                onClick={() => {
                  onDelete(viewer);
                  setViewer(null);
                }}
              />
            ) : (
              <span style={{ width: 44 }} />
            )}
          </div>
          <img src={viewer.url} alt="" />
        </div>
      )}
    </>
  );
}
